import { useAuiState } from '@assistant-ui/react';

import { isPendingToolCallPart } from '#plugin-chat/hooks/isAwaitingInputPart.ts';

type PartLike = Parameters<typeof isPendingToolCallPart>[0];
type ThreadMessageLike = { readonly content?: readonly PartLike[] };

/**
 * `true` when any message part in the thread is a tool call still awaiting
 * its result — a call in flight, including one blocked on a
 * human-in-the-loop callback (`askUserQuestion` awaiting `human()`).
 *
 * Detection keys off `!result` (`isPendingToolCallPart`), the same reliable
 * signal `AskUserQuestionSlides` uses, *not* the `requires-action` /
 * `interrupt` status — that status is not set for the first `human()` call
 * of a tool (upstream assistant-ui race), so a status-based check would
 * miss exactly the first-slide case.
 *
 * While such a call is pending the AI SDK runtime keeps `thread.isRunning`
 * truthy, so assistant-ui's `ComposerPrimitive.Input` swallows Enter into a
 * newline and disables Send. The composer uses this hook to detect that
 * state and offer a cancel-and-send affordance instead;
 * `wrappedChat.sendMessage` does the actual cancel-then-send.
 */
export function useHasPendingToolCall(): boolean {
  return useAuiState(({ thread }) =>
    (thread.messages as readonly ThreadMessageLike[]).some((message) =>
      (message.content ?? []).some(isPendingToolCallPart),
    ),
  );
}
